/**
 * Page layout for the continuous vertical scroller (pure — unit tested). The paged spread layout
 * (spreads.ts: "Heil síða" one page at a time, "Tvær síður" two-up) returns the same Layout shape
 * with kind 'spread'.
 *
 * The base layout (zoom 1) fills the viewport width; at zoom z every coordinate is simply × z, so
 * zooming around a focal point is exact: newScroll = (scroll + focal) · z₂/z₁ − focal.
 */
import type { PageSize } from '../../shared/dto/library';
import type { FitMode, ViewerPosition } from './types';

export interface PageBox {
  top: number;
  left: number;
  width: number;
  height: number;
}

export type LayoutKind = 'scroll' | 'spread';
/** Pages per spread of the paged layout: 1 = "Heil síða" (one whole page), 2 = "Tvær síður" (two-up). */
export type PagesPerSpread = 1 | 2;

export interface Layout {
  /** 'scroll' = all pages stacked vertically; 'spread' = paged mode (see spreads.ts). */
  kind: LayoutKind;
  /** kind 'spread': pages per spread (absent = 2, two-up). Absent in the scroll layout. */
  perSpread?: PagesPerSpread;
  boxes: PageBox[];
  /** Content size at this zoom. */
  width: number;
  height: number;
  zoom: number;
  fit: FitMode;
  /** Vertical padding above the first page (already × zoom). */
  padTop: number;
  viewportW: number;
  viewportH: number;
}

export const PAGE_GAP = 10;
export const PAD_X = 6;
export const PAD_Y = 8;
export const MAX_PAGE_WIDTH = 1400;

export interface LayoutInput {
  pages: readonly PageSize[];
  viewportW: number;
  viewportH: number;
  fit: FitMode;
  zoom: number;
  maxPageWidth?: number;
}

function aspect(p: PageSize): number {
  return p.w > 0 && p.h > 0 ? p.h / p.w : Math.SQRT2;
}

export function computeLayout(input: LayoutInput): Layout {
  const { pages, fit } = input;
  const vw = Math.max(1, input.viewportW);
  const vh = Math.max(1, input.viewportH);
  const z = input.zoom;
  const fitW = Math.max(40, Math.min(vw - 2 * PAD_X, input.maxPageWidth ?? MAX_PAGE_WIDTH));
  const fitH = Math.max(40, vh - 2 * PAD_Y);
  const boxes: PageBox[] = [];
  let y = PAD_Y;
  for (const p of pages) {
    const a = aspect(p);
    let w = fitW;
    if (fit === 'page') w = Math.min(fitW, fitH / a);
    const h = w * a;
    boxes.push({ top: y * z, left: ((vw - w) / 2) * z, width: w * z, height: h * z });
    y += h + PAGE_GAP;
  }
  const height = (pages.length > 0 ? y - PAGE_GAP + PAD_Y : 2 * PAD_Y) * z;
  return { kind: 'scroll', boxes, width: vw * z, height, zoom: z, fit, padTop: PAD_Y * z, viewportW: vw, viewportH: vh };
}

/** Index of the page whose box (incl. the gap below it) contains content-y. */
export function pageAtY(layout: Layout, y: number): number {
  const b = layout.boxes;
  if (b.length === 0) return 0;
  let lo = 0;
  let hi = b.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (b[mid]!.top <= y) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

export interface VisibleInfo {
  first: number;
  last: number;
  /** Page with the largest visible height (shown in the "3 / 12" indicator). */
  current: number;
  /** Visible pages ordered by visible height, largest first. */
  ordered: number[];
}

export function visiblePages(layout: Layout, scrollTop: number, viewportH: number): VisibleInfo {
  const b = layout.boxes;
  if (b.length === 0) return { first: 0, last: -1, current: 0, ordered: [] };
  const top = scrollTop;
  const bottom = scrollTop + viewportH;
  const first = pageAtY(layout, top);
  const vis: { i: number; h: number }[] = [];
  for (let i = first; i < b.length; i++) {
    const box = b[i]!;
    if (box.top >= bottom) break;
    const h = Math.min(bottom, box.top + box.height) - Math.max(top, box.top);
    if (h > 0) vis.push({ i, h });
  }
  if (vis.length === 0) {
    // Viewport lies entirely in a gap/padding: the nearest page counts as current.
    const i = Math.min(b.length - 1, first + (top > b[first]!.top + b[first]!.height ? 1 : 0));
    return { first: i, last: i, current: i, ordered: [i] };
  }
  const ordered = [...vis].sort((a, c) => c.h - a.h || a.i - c.i).map((v) => v.i);
  return { first: vis[0]!.i, last: vis[vis.length - 1]!.i, current: ordered[0]!, ordered };
}

/** Scroll position that puts a page's top just below the padding. */
export function pageScrollTop(layout: Layout, page: number): number {
  const box = layout.boxes[Math.max(0, Math.min(layout.boxes.length - 1, page))];
  if (!box) return 0;
  return Math.max(0, box.top - layout.padTop);
}

export function positionFromScroll(layout: Layout, scrollTop: number, scrollLeft: number): ViewerPosition {
  const page = pageAtY(layout, scrollTop);
  const box = layout.boxes[page];
  const top = box && box.height > 0 ? (scrollTop - box.top) / box.height : 0;
  const left = layout.width > 0 ? scrollLeft / layout.width : 0;
  return { page, top, left, zoom: layout.zoom };
}

/** A position taken in paged mode (layout 'spread') lands at the top of its page. */
export function scrollFromPosition(layout: Layout, pos: ViewerPosition): { top: number; left: number } {
  const box = layout.boxes[Math.max(0, Math.min(layout.boxes.length - 1, pos.page))];
  if (!box) return { top: 0, left: 0 };
  if (pos.layout === 'spread') return { top: pageScrollTop(layout, pos.page), left: 0 };
  return { top: box.top + pos.top * box.height, left: pos.left * layout.width };
}

/** A connector jump never shows less of its target page than this share of the viewport height. */
export const JUMP_MIN_PAGE_SHARE = 0.6;

/**
 * Scroll offsets that bring a normalised page point into view with its page as the current page
 * (the largest visible height, as in the page indicator):
 *  - a page that fits the viewport height shows whole, its top just below the padding;
 *  - a taller page puts the point at ~25 % of the viewport height (room to read what follows), but
 *    never so far down that the target page fills less than JUMP_MIN_PAGE_SHARE of the viewport —
 *    a point low on the page would otherwise make the next page current and cut off the target.
 * Horizontally centred when zoomed in. Unclamped at the end — the browser clamps to the scroll range.
 */
export function scrollForPoint(layout: Layout, page: number, x: number, y: number): { top: number; left: number } {
  const i = Math.max(0, Math.min(layout.boxes.length - 1, page));
  const box = layout.boxes[i];
  if (!box) return { top: 0, left: 0 };
  const px = box.left + clamp01(x) * box.width;
  const py = box.top + clamp01(y) * box.height;
  const vh = layout.viewportH;
  const left = Math.max(0, Math.min(layout.width - layout.viewportW, px - layout.viewportW / 2));
  if (box.height + layout.padTop <= vh) return { top: pageScrollTop(layout, i), left };
  // The target page must cover at least the minimum share of the viewport, below or above.
  const maxTop = box.top + box.height - vh * JUMP_MIN_PAGE_SHARE;
  const minTop = box.top - vh * (1 - JUMP_MIN_PAGE_SHARE);
  const top = Math.max(0, minTop, Math.min(maxTop, py - vh * 0.25));
  return { top, left };
}

/** Scroll that keeps a content point under a focal point when zooming from z1 to z2. */
export function zoomScroll(scroll: number, focal: number, z1: number, z2: number): number {
  return (scroll + focal) * (z2 / z1) - focal;
}

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

export function clampZoom(z: number, min = 1, max = 4): number {
  if (!Number.isFinite(z)) return min;
  return Math.min(max, Math.max(min, z));
}
